import { useState } from "react";
import { Box, Stack } from "@mui/material";
import { COLORS, Text, SecondaryTabs } from "@asayinc/component-library";

import { PageContainer } from "src/components/containers/PageContainer/PageContainer";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { Card } from "src/components/atoms/Card";
import { useCurrentUserQuery } from "src/store/user";
import { LoadingCard } from "src/components/molecules/Loading";
import { UserManagement } from "./components/UserManagement";

type SettingsTab = "profile" | "users";

export function Settings() {
  const { data: user, isLoading } = useCurrentUserQuery();
  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");

  const isSuperAdmin = user?.isIntermediaryPortalSuperAdmin ?? false;

  if (isLoading) {
    return (
      <PageContainer>
        <Text variant="h4" sx={{ pb: 4, pt: 10, px: 10 }}>
          Settings
        </Text>
        <ScrollContainer sx={{ pt: 4, pb: 10, pl: 10, pr: 6.25 }}>
          <LoadingCard />
        </ScrollContainer>
      </PageContainer>
    );
  }

  const solicitors = user?.solicitors || [];
  const tabulators = user?.tabulators || [];
  const claimsAdministrators = user?.claimsAdministrators || [];

  return (
    <PageContainer>
      <Text variant="h4" sx={{ pb: 4, pt: 10, px: 10 }}>
        Settings
      </Text>
      <ScrollContainer sx={{ pt: 4, pb: 10, pl: 10, pr: 6.25 }}>
        {isSuperAdmin && (
          <SecondaryTabs
            sx={{ mb: 6 }}
            tabs={[
              { label: "Profile", value: "profile" },
              { label: "User Management", value: "users" },
            ]}
            activeTab={activeTab}
            clickHandler={(tab) => setActiveTab(tab as SettingsTab)}
          />
        )}

        {activeTab === "profile" && (
          <>
            {/* Profile Section */}
            <Card title="Profile" sx={{ mb: 6 }}>
              <Stack spacing={3}>
                <Box>
                  <Text
                    variant="body3"
                    sx={{ color: COLORS.P_MED_LOW_EMPHASIS, mb: 1 }}
                  >
                    Name
                  </Text>
                  <Text variant="body1" sx={{ fontWeight: 500 }}>
                    {user?.firstName} {user?.lastName}
                  </Text>
                </Box>
                <Box>
                  <Text
                    variant="body3"
                    sx={{ color: COLORS.P_MED_LOW_EMPHASIS, mb: 1 }}
                  >
                    Email
                  </Text>
                  <Text variant="body1" sx={{ fontWeight: 500 }}>
                    {user?.email}
                  </Text>
                </Box>
                {(user?.isIntermediaryPortalSuperAdmin ||
                  user?.isIntermediaryPortalAdmin) && (
                  <Box>
                    <Text
                      variant="body3"
                      sx={{ color: COLORS.P_MED_LOW_EMPHASIS, mb: 1 }}
                    >
                      Role
                    </Text>
                    <Text
                      variant="body1"
                      sx={{ fontWeight: 500, color: "#1968FF" }}
                    >
                      {user?.isIntermediaryPortalSuperAdmin
                        ? "Super Admin"
                        : "Admin"}
                    </Text>
                  </Box>
                )}
              </Stack>
            </Card>

            {/* Associated Companies Section */}
            <Card title="Associated Companies" sx={{ mb: 6 }}>
              <Text
                variant="body3"
                sx={{ color: COLORS.P_MED_LOW_EMPHASIS, mb: 4 }}
              >
                Companies you are associated with in the intermediary portal
              </Text>

              {/* Solicitors */}
              {solicitors.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Text variant="subtitle2" sx={{ mb: 2 }}>
                    Solicitors
                  </Text>
                  <Stack spacing={2}>
                    {solicitors.map((solicitor) => (
                      <Box
                        key={solicitor.id}
                        sx={{
                          p: 3,
                          borderRadius: 1,
                          border: `1px solid ${COLORS.P_OUTLINE}`,
                          backgroundColor: COLORS.WHITE,
                        }}
                      >
                        <Text variant="body2" sx={{ fontWeight: 500 }}>
                          {solicitor.name}
                        </Text>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Tabulators */}
              {tabulators.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Text variant="subtitle2" sx={{ mb: 2 }}>
                    Tabulators
                  </Text>
                  <Stack spacing={2}>
                    {tabulators.map((tabulator) => (
                      <Box
                        key={tabulator.id}
                        sx={{
                          p: 3,
                          borderRadius: 1,
                          border: `1px solid ${COLORS.P_OUTLINE}`,
                          backgroundColor: COLORS.WHITE,
                        }}
                      >
                        <Text variant="body2" sx={{ fontWeight: 500 }}>
                          {tabulator.name}
                        </Text>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Claims Administrators */}
              {claimsAdministrators.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Text variant="subtitle2" sx={{ mb: 2 }}>
                    Claims Administrators
                  </Text>
                  <Stack spacing={2}>
                    {claimsAdministrators.map((ca) => (
                      <Box
                        key={ca.id}
                        sx={{
                          p: 3,
                          borderRadius: 1,
                          border: `1px solid ${COLORS.P_OUTLINE}`,
                          backgroundColor: COLORS.WHITE,
                        }}
                      >
                        <Text variant="body2" sx={{ fontWeight: 500 }}>
                          {ca.name}
                        </Text>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {solicitors.length === 0 &&
                tabulators.length === 0 &&
                claimsAdministrators.length === 0 && (
                  <Text
                    variant="body2"
                    sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}
                  >
                    You are not associated with any companies.
                  </Text>
                )}
            </Card>
          </>
        )}

        {activeTab === "users" && isSuperAdmin && <UserManagement />}
      </ScrollContainer>
    </PageContainer>
  );
}
